import { Facility, FacilityStatus } from "encamp-prisma/generated/browser";

type PartialNullable<T> = { [P in keyof T]?: T[P] | undefined | null };

/** Format a date for use in NNR reason text (e.g. "Closed as of 12/10/2024"). */
function formatReasonDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

export type FormattedNotReportingReasonOptions = {
  reportingYear?: number;
};

/**
 * Returns the full sentence reason for newly-not-reporting emails (e.g. "Closed as of 12/10/2024"
 * or "Closed for the entire reporting year"). Matches the copy in the NNR cover letter reasons block.
 * Use when building TemplateFacility.newlyNotReportingReason.
 */
export function getFormattedOakNotReportingReason(
  facility?: PartialNullable<Facility>,
  options?: FormattedNotReportingReasonOptions
): string | null {
  if (!facility) return null;

  const reportingYear = options?.reportingYear;
  const fallbackSuffix = reportingYear != null ? " for the entire reporting year" : "";

  if (facility.status === FacilityStatus.CLOSED) {
    if (facility.dateClosed) {
      return `Closed as of ${formatReasonDate(facility.dateClosed)}`;
    }
    return `Closed${fallbackSuffix}`.trim() || "Closed";
  }

  if (facility.colocationReportingFacilityId) {
    return "Co-located with another reporting facility on the same site";
  }

  if (facility.isNotReporting) {
    return "Chemical inventory was below reporting thresholds for the entire reporting year";
  }

  if (facility.status === FacilityStatus.INACTIVE) {
    if (facility.dateInactive) {
      return `Inactive as of ${formatReasonDate(facility.dateInactive)}`;
    }
    return `Inactive${fallbackSuffix}`.trim() || "Inactive";
  }

  if (facility.isNotReportingThroughEncamp) {
    return "No longer reporting through Encamp";
  }

  return null;
}

/**
 * Minimal shape needed to derive a short not-reporting reason. `status` is typed
 * as a string so callers holding EITHER the Prisma `FacilityStatus` enum (backend)
 * OR the Oak GraphQL `FacilityStatus` enum (frontend) can pass it directly — both
 * enums share the same string values ("CLOSED"/"INACTIVE"/…), so no cast is needed.
 */
export type NotReportingReasonFacility = {
  status?: string | null;
  isNotReporting?: boolean | null;
  isNotReportingThroughEncamp?: boolean | null;
  colocationReportingFacilityId?: string | null;
};

export function getOakNotReportingReason(facility?: NotReportingReasonFacility) {
  if (!facility) return null;

  if (facility.status === FacilityStatus.CLOSED) {
    return "Closed";
  }

  if (facility.colocationReportingFacilityId) {
    return "Co-located";
  }

  if (facility.isNotReporting) {
    return "Under Thresholds";
  }

  if (facility.status === FacilityStatus.INACTIVE) {
    return "Is Inactive";
  }

  if (facility.isNotReportingThroughEncamp) {
    return "Without Encamp";
  }

  return null;
}
